import * as React from "react"
import { env } from "../../config"
import { captureError } from "../../util/posthog/capture-error"

declare global {
  interface Window {
    onMapsLoaded?: () => void
    google: typeof google
  }
}

let googleMapsLoaded = false
let googleMapsScriptRequested = false
const googleMapsListeners = new Set<() => void>()
const GOOGLE_MAPS_CALLBACK = "onMapsLoaded"

type UseGoogleMapsOptions = {
  enabled?: boolean
}

function notifyGoogleMapsLoaded() {
  googleMapsLoaded = true
  googleMapsListeners.forEach((listener) => listener())
}

/**
 * With `loading=async`, the bootstrap defines `google.maps` before the Places
 * library arrives, so only `google.maps.places` means address fields can work.
 */
function getLoadedGoogle(): typeof window.google | null {
  return typeof window !== "undefined" && window.google?.maps?.places
    ? window.google
    : null
}

function importPlacesLibrary(maps: typeof google.maps) {
  googleMapsScriptRequested = true
  maps
    .importLibrary("places")
    .then(notifyGoogleMapsLoaded, (error: unknown) => {
      // Let the next address-field interaction retry the import.
      googleMapsScriptRequested = false
      captureError(error, { google_maps_load: "places_import" })
    })
}

function loadGoogleMaps() {
  const GOOGLE_MAPS_API_KEY = env("GOOGLE_MAPS_API_KEY")

  if (!googleMapsScriptRequested) {
    googleMapsScriptRequested = true
    window.onMapsLoaded = notifyGoogleMapsLoaded

    const googleMapsScript = document.createElement("script")

    googleMapsScript.setAttribute("id", "google-maps")
    googleMapsScript.setAttribute(
      "src",
      `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&language=en&callback=${GOOGLE_MAPS_CALLBACK}&loading=async`,
    )
    googleMapsScript.onerror = () => {
      // Let the next address-field interaction retry the load.
      googleMapsScriptRequested = false
      googleMapsScript.remove()
      captureError(new Error("Google Maps script failed to load"), {
        google_maps_load: "script",
      })
    }
    document.head.appendChild(googleMapsScript)
  }
}

/**
 * Starts the Places script on the first address-field interaction. Every
 * mounted `useGoogleMaps()` consumer is notified once it loads.
 */
export function requestGoogleMaps(): void {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return
  }

  // Another loader may have brought in Places after a form subscribed.
  if (getLoadedGoogle()) {
    notifyGoogleMapsLoaded()

    return
  }

  if (googleMapsScriptRequested) {
    return
  }

  const maps = window.google?.maps

  // Another loader brought in Maps without Places.
  if (typeof maps?.importLibrary === "function") {
    importPlacesLibrary(maps)

    return
  }

  loadGoogleMaps()
}

/**
 * Subscribes to the Places script without loading it; place inputs call
 * `requestGoogleMaps()` on first use. Pass `enabled: true` to load on mount.
 */
export function useGoogleMaps({ enabled = false }: UseGoogleMapsOptions = {}): {
  mapsLoaded: boolean
  google: typeof window.google | null
} {
  const [mapsLoaded, setMapsLoaded] = React.useState(
    () => googleMapsLoaded && Boolean(getLoadedGoogle()),
  )
  const [google, setGoogle] = React.useState<typeof window.google | null>(
    getLoadedGoogle,
  )
  const onMapsLoaded = React.useCallback(() => {
    setMapsLoaded(true)
    setGoogle(getLoadedGoogle())
  }, [])

  React.useEffect(() => {
    if (getLoadedGoogle()) {
      googleMapsLoaded = true
      onMapsLoaded()

      return
    }

    googleMapsListeners.add(onMapsLoaded)

    if (enabled) {
      requestGoogleMaps()
    }

    return () => {
      googleMapsListeners.delete(onMapsLoaded)
    }
  }, [enabled, onMapsLoaded])

  return {
    mapsLoaded,
    google,
  }
}

type PlaceRequest = Omit<google.maps.places.AutocompletionRequest, "input">

type PlaceMatch = {
  description: string
  placeId: string
}

type AddressCheck = {
  request: PlaceRequest
  onMatch: (description: string, placeId: string) => unknown
  onNoMatch: () => unknown
}

type AddressReplay = {
  suggest: (text: string) => unknown
  validate?: AddressCheck
}

/**
 * Asks Places for suggestions for `text` and resolves the one whose
 * description matches it exactly (ignoring surrounding whitespace); null when
 * none does or the lookup fails.
 * Lets a blur check validate text no suggestions were fetched for (a paste).
 */
function findExactPlace(
  text: string,
  request: PlaceRequest,
): Promise<PlaceMatch | null> {
  const input = text.trim()

  return new Promise((resolve) => {
    new google.maps.places.AutocompleteService().getPlacePredictions(
      { ...request, input },
      (results) => {
        const match = results?.find(
          (r) => r.description.toLowerCase() === input.toLowerCase(),
        )

        if (!match) {
          resolve(null)

          return
        }

        resolve({ description: match.description, placeId: match.place_id })
      },
    )
  })
}

/**
 * Validates text left in an address input with Places, unless the visitor
 * changed the input's text before Places answered.
 */
export function checkAddressWithPlaces(
  text: string,
  inputRef: React.RefObject<HTMLInputElement | null>,
  check: AddressCheck,
): void {
  findExactPlace(text, check.request).then((match) => {
    const textChanged = inputRef.current?.value !== text

    if (textChanged) {
      return
    }

    if (match) {
      check.onMatch(match.description, match.placeId)
    } else {
      check.onNoMatch()
    }
  })
}

/**
 * Picks up an address typed while the Places script was still loading: shows
 * suggestions if the input is still focused, or runs the blur check the input
 * deferred by setting the returned ref. Declare it after the effect that
 * creates the AutocompleteService.
 */
export function useReplayAddressOnMapsLoad(
  mapsLoaded: boolean,
  inputRef: React.RefObject<HTMLInputElement | null>,
  replay: AddressReplay,
): React.MutableRefObject<boolean> {
  const wasMapsLoadedRef = React.useRef(mapsLoaded)
  const pendingBlurValidationRef = React.useRef(false)
  const replayRef = React.useRef(replay)

  replayRef.current = replay

  React.useEffect(() => {
    const mapsJustLoaded = mapsLoaded && !wasMapsLoadedRef.current
    const { suggest, validate } = replayRef.current

    wasMapsLoadedRef.current = mapsLoaded
    const text = inputRef.current?.value ?? ""

    if (!mapsJustLoaded || !text.trim()) {
      return
    }

    if (document.activeElement === inputRef.current) {
      suggest(text)

      return
    }

    if (!pendingBlurValidationRef.current || !validate) {
      return
    }

    pendingBlurValidationRef.current = false
    checkAddressWithPlaces(text, inputRef, validate)
  }, [inputRef, mapsLoaded])

  return pendingBlurValidationRef
}
